import { Dispatch, FC, MutableRefObject, ReactNode, SetStateAction, createContext, createRef, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FileManipulationKey, VisualFileType, VisualStatus } from 'constants/visual'
import { Nullable, NullableStringIndexSignature } from 'models/helpers'

import { APIRequestErrorType, APIRequestState } from 'constants/API'
import { APIRequest } from 'models/API'
import { FileState } from 'components/common/FileAPI'
import { VisualClientDTO } from 'models/visuals'
import { useAuth0 } from 'utils/auth'
import { useGalleryOrder } from './GalleryOrder.context'
import { useGalleryVisualType } from './GalleryVisualType.context'
import { useGalleryVisualsMeta } from './GalleryVisualsMeta.context'
import { useGalleryVisualsFileAPI } from './GalleryVisualsFileAPI.context'
import { useVisualStore, DownloadImage, UploadImage } from 'stores/visualStore'

type UploadImageStore = Nullable<NullableStringIndexSignature<NullableStringIndexSignature<UploadImage>>>
type DownloadImageStore = Nullable<NullableStringIndexSignature<NullableStringIndexSignature<DownloadImage>>>

interface GalleryVisualsInterface {
  uploadVisuals?: UploadImageStore
  downloadVisualsSelector?: DownloadImageStore
  downloadVisuals?: DownloadImageStore
  downloadVisualsRef: MutableRefObject<DownloadImageStore>
  downloadVisualsEntries: [string, Nullable<NullableStringIndexSignature<DownloadImage>>][]
  uploadVisualsEntries: [string, Nullable<NullableStringIndexSignature<UploadImage>>][]
  downloadVisualsKeys: string[]
  uploadVisualsKeys: string[]
  downloadVisualsCount: number
  uploadVisualsCount: number
  uploadVisualsKeyToOriginalNameDictionary: Record<string, string>
  downloadVisualsKeyToOriginalNameDictionary: Record<string, string>
  uploadVisualsOriginalFilenames: string[]
  favoritedVisuals: Set<string>
  visualsKeys: string[]
  visualsCount: number
  listedVisuals: VisualClientDTO[]
  listedVisualsKeyToOriginalNameDictionary: Record<string, string>
  visualsKeyToOriginalNameDictionary: Record<string, string>
  downloadVisualsWithErrorEntries: [string, Nullable<NullableStringIndexSignature<DownloadImage>>][],
  setFavoritedVisuals: Dispatch<SetStateAction<Set<string>>>
  unPurchasedFavoritedVisualsLength: number
  purchasedFavoritedVisualsLength: number
  visualMetadataMap: Record<string, VisualClientDTO>
}

const defaultGalleryVisualsValue: GalleryVisualsInterface = {
  downloadVisualsRef: createRef(),
  downloadVisualsEntries: [],
  uploadVisualsEntries: [],
  downloadVisualsKeys: [],
  uploadVisualsKeys: [],
  downloadVisualsCount: 0,
  uploadVisualsCount: 0,
  uploadVisualsKeyToOriginalNameDictionary: {},
  downloadVisualsKeyToOriginalNameDictionary: {},
  uploadVisualsOriginalFilenames: [],
  visualsKeys: [],
  visualsCount: 0,
  listedVisuals: [],
  listedVisualsKeyToOriginalNameDictionary: {},
  visualsKeyToOriginalNameDictionary: {},
  downloadVisualsWithErrorEntries: [],
  favoritedVisuals: new Set(),
  setFavoritedVisuals: () => { throw new Error('setFavoritedVisuals is undefined') },
  unPurchasedFavoritedVisualsLength: 0,
  purchasedFavoritedVisualsLength: 0,
  visualMetadataMap: {}
}

/** Gallery visuals context */
export const GalleryVisualsContext = createContext<GalleryVisualsInterface>(defaultGalleryVisualsValue)
/** Gallery visuals context hook */
export const useGalleryVisuals = (): GalleryVisualsInterface => useContext(GalleryVisualsContext)

/** Context provider for gallery visuals */
export const GalleryVisualsContextProvider: FC<{
  assignmentId: string
  children?: ReactNode
}> = ({
  assignmentId,
  children,
}) => {
    const { roles } = useAuth0()

    const {
      webType,
      thumbnailType,
      normalizedOriginalType,
    } = useGalleryVisualType()

    const {
      sortFunction,
      sortEntriesFunction,
    } = useGalleryOrder()

    const {
      listVisualsQuery,
      allVisualsData,
      allVisualsFilenames,
      purchasedVisualsKeys,
    } = useGalleryVisualsMeta()

    // FileAPI provides download state - replaces Zustand download subscription
    const { currentlyVisibleVisuals } = useGalleryVisualsFileAPI()

    // Keep Zustand for uploads only
    const rawUploadVisuals = useVisualStore(state => state.visuals[assignmentId]?.[FileManipulationKey.UPLOAD])
    const removeUploadWrapper = useVisualStore(state => state.removeUploadWrapper)

    // Normalize filename by stripping extension for matching
    // API returns names without extension, upload keys may have extension
    const stripExt = (name: string) => name.replace(/\.[^.]+$/, '')

    // Set of base filenames from API response (without extension)
    const apiBaseFilenames = useMemo(
      () => new Set(allVisualsFilenames.map(stripExt)),
      [allVisualsFilenames]
    )

    // Set of base filenames that have a loaded displayUrl in FileAPI (image ready to show)
    const readyFileAPIFilenames = useMemo(
      () => new Set(currentlyVisibleVisuals.filter(v => !!v.displayUrl).map(v => stripExt(v.gcFilename ?? ''))),
      [currentlyVisibleVisuals]
    )

    // Check if an upload key matches any API filename (extension-agnostic)
    const isInApi = (key: string) => apiBaseFilenames.has(stripExt(key))
    // Check if the visual is ready to display in FileAPI (has loaded image)
    const isReadyInFileAPI = (key: string) => readyFileAPIFilenames.has(stripExt(key))

    // Normalize upload keys to base name (strip extension) so they match download keys
    // Upload entries are cleaned up imperatively by useVisualOperations after query invalidation
    const uploadVisuals = useMemo(() => {
      if (!rawUploadVisuals) return rawUploadVisuals
      const normalized: typeof rawUploadVisuals = {}
      let hasEntries = false
      for (const [key, value] of Object.entries(rawUploadVisuals)) {
        if (value) {
          normalized[stripExt(key)] = value
          hasEntries = true
        }
      }
      return hasEntries ? normalized : null
    }, [rawUploadVisuals])

    // Set of base filenames still showing as upload cards (not yet ready in FileAPI)
    // Keys are already normalized (extension stripped) in uploadVisuals
    const activeUploadBaseNames = useMemo(() => {
      if (!uploadVisuals) return new Set<string>()
      return new Set(Object.keys(uploadVisuals))
    }, [uploadVisuals])

    // Track files being deleted (from Zustand store)
    const deletingFilenames = useVisualStore(state => state.deletingFiles[assignmentId])

    // Transform FileAPI entries into legacy DownloadImage format for backward compatibility
    const downloadVisuals: DownloadImageStore = useMemo(() => {
      const store: NullableStringIndexSignature<NullableStringIndexSignature<DownloadImage>> = {}

      for (const file of currentlyVisibleVisuals) {
        const filename = file.gcFilename
        if (!filename) continue

        // Skip entries that still have an active (in-progress) upload card to prevent duplicates
        // Once upload is complete (progress 100), allow download entry to coexist — controller will pick the right one
        const uploadEntry = activeUploadBaseNames.has(stripExt(filename))
          ? rawUploadVisuals?.[filename]?.[normalizedOriginalType] ?? rawUploadVisuals?.[stripExt(filename)]?.[normalizedOriginalType]
          : undefined
        if (uploadEntry && (uploadEntry.progress ?? 0) < 100) continue

        const isError = file.state === FileState.ERROR
        const isSuccess = file.state === FileState.SUCCESS
        const isRunning = file.state === FileState.RUNNING
        const isDeleting = deletingFilenames?.has(filename) ?? false

        const request = isError
          ? APIRequest.factoryError<null>(APIRequestErrorType.CLIENT_ERROR, file.error)
          : isSuccess || file.displayUrl
            ? APIRequest.factoryOK<null>(null)
            : isRunning
              ? APIRequest.factoryRunning<null>(null)
              : new APIRequest<null>()

        const downloadImage: DownloadImage = {
          file: new File([], filename),
          type: thumbnailType,
          signedUrl: file.displayUrl ?? undefined,
          originalFilename: file.metadata.originalName ?? file.originalFilename ?? undefined,
          request,
          lastUpdated: Date.now(),
          deleting: isDeleting,
        }

        // Provide the same image entry for both thumbnail and web type keys
        // Consumers access allTypes?.[thumbnailType] and allTypes?.[webType]
        store[filename] = {
          [thumbnailType]: { ...downloadImage, type: thumbnailType },
          [webType]: { ...downloadImage, type: webType },
        }

        // For clients, also provide POST_THUMB and POST_THUMB_WATERMARK entries
        if (roles.isClient) {
          const isPurchased = purchasedVisualsKeys.has(filename)
          if (isPurchased) {
            store[filename]![VisualFileType.POST_THUMB] = { ...downloadImage, type: VisualFileType.POST_THUMB }
            store[filename]![VisualFileType.POST_WEB] = { ...downloadImage, type: VisualFileType.POST_WEB }
          } else {
            store[filename]![VisualFileType.POST_THUMB_WATERMARK] = { ...downloadImage, type: VisualFileType.POST_THUMB_WATERMARK }
          }
        }
      }

      return store
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rawUploadVisuals changes on every progress tick; activeUploadBaseNames already gates recomputation
    }, [currentlyVisibleVisuals, thumbnailType, webType, roles.isClient, purchasedVisualsKeys, activeUploadBaseNames, deletingFilenames])

    const downloadVisualsRef = useRef<DownloadImageStore>(downloadVisuals)
    downloadVisualsRef.current = downloadVisuals

    const downloadVisualsEntries = useMemo(() => {
      if (!downloadVisuals) return []

      return Object.entries(downloadVisuals)
        .filter(([_, allTypes]) => {
          if (!roles.isClient) {
            return !!allTypes?.[thumbnailType]
          }

          return !!(allTypes?.[VisualFileType.POST_THUMB] || allTypes?.[VisualFileType.POST_THUMB_WATERMARK])
        })
        .sort(sortEntriesFunction)
    }, [downloadVisuals, roles.isClient, sortEntriesFunction, thumbnailType])

    const uploadVisualsEntries = useMemo(() => (uploadVisuals
      ? Object.entries(uploadVisuals)
        .filter(([_, allTypes]) => allTypes?.[normalizedOriginalType])
        .sort(([, a], [, b]) => {
          const aTime = a?.[normalizedOriginalType]?.droppedIn ?? 0
          const bTime = b?.[normalizedOriginalType]?.droppedIn ?? 0
          return aTime - bTime
        })
      : []), [uploadVisuals, normalizedOriginalType])
    const downloadVisualsKeys = useMemo(() => {
      return downloadVisualsEntries
        .filter(([_, allTypes]) => {
          if (!roles.isClient) {
            return !!allTypes?.[thumbnailType]
          }

          return !!(allTypes?.[VisualFileType.POST_THUMB] || allTypes?.[VisualFileType.POST_THUMB_WATERMARK])
        })
        .map(([key]) => key)
    }, [downloadVisualsEntries, roles.isClient, thumbnailType])
    const uploadVisualsKeys = useMemo(() => (uploadVisualsEntries.filter(([_, allTypes]) => allTypes?.[normalizedOriginalType]).map(([key]) => key)), [uploadVisualsEntries, normalizedOriginalType])
    const downloadVisualsCount = useMemo(() => (downloadVisualsKeys.length), [downloadVisualsKeys])
    const uploadVisualsCount = useMemo(() => (uploadVisualsKeys.length), [uploadVisualsKeys])
    const uploadVisualsKeyToOriginalNameDictionary = useMemo(() => Object.fromEntries(uploadVisualsEntries.map(([key, allTypes]) => [key, allTypes?.[normalizedOriginalType]?.originalFilename || ''] as [string, string]).filter(([, originalName]) => !!originalName)), [uploadVisualsEntries, normalizedOriginalType])
    const downloadVisualsKeyToOriginalNameDictionary = useMemo(() => Object.fromEntries(downloadVisualsEntries.map(([key, allTypes]) => [key, allTypes?.[normalizedOriginalType]?.originalFilename || ''] as [string, string]).filter(([, originalName]) => !!originalName)), [downloadVisualsEntries, normalizedOriginalType])
    const uploadVisualsOriginalFilenames = useMemo(() => Object.values(uploadVisualsKeyToOriginalNameDictionary), [uploadVisualsKeyToOriginalNameDictionary])
    const visualsKeys = useMemo(() => [...uploadVisualsKeys, ...downloadVisualsKeys].sort(sortFunction), [uploadVisualsKeys, downloadVisualsKeys, sortFunction])
    const visualsCount = useMemo(() => visualsKeys.length, [visualsKeys])
    const listedVisuals = useMemo(() => allVisualsData ? Object.values((allVisualsData.visuals as VisualClientDTO[])).sort((visualA, visualB) => sortFunction(visualA.name, visualB.name)) : [], [allVisualsData, sortFunction])
    const listedVisualsKeyToOriginalNameDictionary = useMemo(() => Object.fromEntries(listedVisuals.filter(visual => !!visual.originalName).map(visual => [visual.name, visual.originalName || ''] as [string, string])), [listedVisuals])
    const visualsKeyToOriginalNameDictionary: typeof listedVisualsKeyToOriginalNameDictionary = useMemo(() => { return { ...listedVisualsKeyToOriginalNameDictionary, ...downloadVisualsKeyToOriginalNameDictionary, ...uploadVisualsKeyToOriginalNameDictionary } }, [listedVisualsKeyToOriginalNameDictionary, downloadVisualsKeyToOriginalNameDictionary, uploadVisualsKeyToOriginalNameDictionary])
    const downloadVisualsWithErrorEntries = useMemo(() => downloadVisualsEntries.filter(([, allTypes]) => {
      if (!roles.isClient) {
        return allTypes?.[thumbnailType]?.request.state === APIRequestState.ERROR
      }

      const postThumbError = allTypes?.[VisualFileType.POST_THUMB]?.request.state === APIRequestState.ERROR
      const postThumbWatermarkError = allTypes?.[VisualFileType.POST_THUMB_WATERMARK]?.request.state === APIRequestState.ERROR

      return postThumbError || postThumbWatermarkError
    }), [downloadVisualsEntries, roles.isClient, thumbnailType])

    const fetchedFavoritedVisuals = useMemo(() => {
      if (allVisualsData) {
        return (allVisualsData.visuals as VisualClientDTO[]).reduce((acc: Set<string>, item: VisualClientDTO) => {
          if (item.favorited) acc.add(item.name)
          return acc
        }, new Set<string>())
      }

      return new Set<string>()
    }, [allVisualsData])

    const visualMetadataMap: Record<string, VisualClientDTO> = useMemo(() => {
      const visuals = (allVisualsData?.visuals ?? []) as VisualClientDTO[]

      return visuals.reduce((acc: Record<string, VisualClientDTO>, item: VisualClientDTO) => {
        acc[item.name] = item

        return acc
      }, {}) ?? {}
    }, [allVisualsData])

    const [favoritedVisuals, setFavoritedVisuals] = useState<Set<string>>(fetchedFavoritedVisuals)

    useEffect(() => {
      if (allVisualsData) {
        setFavoritedVisuals(fetchedFavoritedVisuals)
      }
    }, [allVisualsData, fetchedFavoritedVisuals])

    const unPurchasedFavoritedVisualsLength = useMemo(() => (
      [...favoritedVisuals].filter(visual => !purchasedVisualsKeys.has(visual))
        .reduce((unpurchasedSet, visual) => unpurchasedSet.add(visual), new Set())
        .size
    ), [favoritedVisuals, purchasedVisualsKeys])

    const purchasedFavoritedVisualsLength = useMemo(() => (
      [...favoritedVisuals].filter(visual => purchasedVisualsKeys.has(visual))
        .reduce((purchasedSet, visual) => purchasedSet.add(visual), new Set())
        .size
    ), [favoritedVisuals, purchasedVisualsKeys])

    // If there are visuals in processing state, we will refetch the listVisuals every 15 seconds
    useEffect(() => {
      const allVisualsMetadata = Object.values(visualMetadataMap)

      if (allVisualsMetadata.some(visual => visual.status === VisualStatus.PROCESSING)) {
        const interval = setInterval(() => {
          listVisualsQuery.refetch()
        }, 15000)

        return () => clearInterval(interval)
      }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visualMetadataMap])

    return (
      <GalleryVisualsContext.Provider
        value={{
          uploadVisuals,
          downloadVisualsSelector: downloadVisuals,
          downloadVisuals,
          downloadVisualsRef,
          downloadVisualsEntries,
          uploadVisualsEntries,
          downloadVisualsKeys,
          uploadVisualsKeys,
          downloadVisualsCount,
          uploadVisualsCount,
          uploadVisualsKeyToOriginalNameDictionary,
          downloadVisualsKeyToOriginalNameDictionary,
          uploadVisualsOriginalFilenames,
          favoritedVisuals,
          setFavoritedVisuals,
          visualsKeys,
          visualsCount,
          listedVisuals,
          listedVisualsKeyToOriginalNameDictionary,
          visualsKeyToOriginalNameDictionary,
          downloadVisualsWithErrorEntries,
          unPurchasedFavoritedVisualsLength,
          purchasedFavoritedVisualsLength,
          visualMetadataMap
        }}
      >
        {children}
      </GalleryVisualsContext.Provider>
    )
  }
